import Keycloak from 'keycloak-js/lib/keycloak';
import { setCookie, eraseCookie, fetchUsersModules } from './authentication';
import { clearAuthenticationStorage } from '../util/localStorageUtils';
import {
  postLoginSuccess,
  postLoginFail,
  fetchUsersModulesSuccess,
  setCurrentProductLogin
} from '../redux/actions/authentication';
import { fetchResources } from './authentication';
import { get } from 'lodash';
import { COOKIE_NAME } from '../util/GlobalConstant';
import { filterRole, sortProductsOptions } from './util';

const keycloakConfig = {
  realm: localStorage.getItem('keycloak-realm'),
  url: localStorage.getItem('keycloak-url'),
  clientId: localStorage.getItem('keycloak-client-id')
};
const _kc = new Keycloak(keycloakConfig);
const CM_APPLICATION_ID = 7;

const POST_LOGOUT_REDIRECT_URI = window.location.origin + '/';

const handleAuthenticationFailure = (errorMessage, store) => {
  console.error(errorMessage);
  store.dispatch(postLoginFail({ message: errorMessage }));
  // Logout from Keycloak and redirect to login first
  _kc.logout({ redirectUri: window.location.origin + '/login' });
  // Clear all storage after logout
  clearAuthenticationStorage();
};

/**
 * Initializes Keycloak instance and calls the provided callback function if successfully authenticated.
 *
 * @param onAuthenticatedCallback
 */
const initKeycloak = (onAuthenticatedCallback, store) => {
  _kc
    .init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      pkceMethod: 'S256'
    })
    .then(async (authenticated) => {
      if (!authenticated) {
        console.log('user is not authenticated..!');
        store.dispatch(postLoginFail('user is not authenticated'));
        clearAuthenticationStorage();
        doLogin();
      } else {
        localStorage.setItem('id_token', _kc.token);
        localStorage.setItem('notification-status', true);
        localStorage.setItem('userName', _kc.tokenParsed?.preferred_username);
        setCookie(COOKIE_NAME, _kc.token, 1);

        const state = store.getState();

        let selectedProduct = get(
          state,
          'authentication.currentProductLogin',
          null
        );

        const usersModulesResponse = await fetchUsersModules();

        if (usersModulesResponse.status === 200 && usersModulesResponse.data) {
          const modules = sortProductsOptions(usersModulesResponse.data || []);
          const usersModules = filterRole(modules);

          // Check if user has any valid roles after filtering
          if (!usersModules || usersModules.length === 0) {
            handleAuthenticationFailure(
              'Access Denied: No valid roles assigned to your account. Please contact your administrator.',
              store
            );
            return;
          }

          const roleCode = localStorage.getItem('roleCode');
          store.dispatch(fetchUsersModulesSuccess(usersModules));
          selectedProduct =
            usersModules.find((m) => m.roleCode === roleCode) ||
            usersModules.find(({ isDefault }) => isDefault) ||
            usersModules[0];
          store.dispatch(setCurrentProductLogin(selectedProduct));
        }

        console.log(selectedProduct, 'selectedProduct');

        const resourceResponse = await fetchResources(
          selectedProduct ? selectedProduct.applicationId : CM_APPLICATION_ID,
          selectedProduct ? selectedProduct.roleCode : null
        );

        if (resourceResponse.status === 200) {
          const resourceDataList = resourceResponse.data.policies;
          store.dispatch(postLoginSuccess(resourceDataList));
        }
      }
      onAuthenticatedCallback();
    })
    .catch((error) => {
      console.error('UserService: Keycloak init failed:', error);
      clearAuthenticationStorage();
      onAuthenticatedCallback();
    });
};

const doLogin = () => {
  // Optionally adding logging or pre-login actions here
  console.log('UserService: Initiating login...');
  _kc.login();
};

const doLogout = () => {
  console.log(
    'UserService: Keyclaok Post Logout Redirect URI:',
    POST_LOGOUT_REDIRECT_URI
  );
  // --- End Cleanup ---
  // Call the actual Keycloak logout function with the required redirectUri
  _kc.logout({ redirectUri: POST_LOGOUT_REDIRECT_URI });
  clearAuthenticationStorage();
};

const getToken = () => _kc.token;

const getTokenParsed = () => _kc.tokenParsed;

const isAuthenticated = () => _kc.authenticated;

const isLoggedIn = () => !!_kc.token;

const updateToken = async () => {
  try {
    const refreshed = await _kc.updateToken(30);
    if (refreshed) {
      localStorage.setItem('id_token', _kc.token);
    }
    return _kc.token;
  } catch (error) {
    console.error('Token refresh failed:', error);
    // Store error message before clearing storage
    localStorage.setItem(
      'auth_error_message',
      'Session expired. Please log in again.'
    );
    clearAuthenticationStorage(true);
    // Only redirect if not already on login page
    if (!window.location.pathname.includes('/login')) {
      _kc.logout({ redirectUri: window.location.origin + '/login' });
    }
    throw error; // Let the caller handle the error
  }
};

const getUsername = () => _kc.tokenParsed?.preferred_username;

const getUserRoles = () => {
  if (_kc.tokenParsed?.realm_access?.roles) {
    return _kc.tokenParsed.realm_access.roles;
  }
  return [];
};

const UserService = {
  _kc,
  initKeycloak,
  doLogin,
  doLogout,
  isAuthenticated,
  isLoggedIn,
  getToken,
  getTokenParsed,
  updateToken,
  getUsername,
  getUserRoles
};

export default UserService;
